import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, ExternalLink, MapPin, Video, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { URBAN_FILM_FESTIVAL_SCHEDULE, URBAN_FILM_FESTIVAL_SOURCE, URBAN_FILM_FESTIVAL_REGISTRATION } from "@/data/urbanFilmFestival";
import ismDiamond from "@assets/IMG_3411_1779819410701.png";
import { format, parseISO } from "date-fns";

export default function UrbanFilmFestival() {
  const dates = Object.keys(URBAN_FILM_FESTIVAL_SCHEDULE).sort();
  const base = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

  return (
    <div className="min-h-screen bg-background text-foreground pb-24 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-x-0 top-0 h-[760px] pointer-events-none z-0">
        <img src={`${base}/uff-slogan.jpg`} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/25 via-background/80 to-background" />
      </div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 opacity-10 blur-[100px] pointer-events-none z-0">
        <img src={ismDiamond} alt="" className="w-[1000px] h-[1000px] object-cover" />
      </div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/20 blur-[150px] rounded-full pointer-events-none z-0" />

      {/* Hero Section */}
      <div className="relative z-10 container mx-auto px-4 pt-24 pb-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl mx-auto text-center space-y-6"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-mono uppercase tracking-widest mb-6">
            <Video className="w-4 h-4" />
            <span>Presented by Florida Film House</span>
          </div>

          <img
            src={`${base}/uff-logo-color.png`}
            alt="Urban Film Festival"
            className="w-56 md:w-72 mx-auto object-contain drop-shadow-[0_10px_35px_rgba(0,0,0,.65)]"
          />
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter uppercase leading-[0.9]">
            Urban Film <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">Festival Miami</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            September 4–5, 2026. Two published festival days of independent cinema, filmmaker conversations, networking, and culture in Miami.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <a href={URBAN_FILM_FESTIVAL_REGISTRATION} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto text-sm font-bold uppercase tracking-widest px-8 h-14 bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                <Ticket className="w-4 h-4" />
                Register Now
              </Button>
            </a>
            <a href={URBAN_FILM_FESTIVAL_SOURCE} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto text-sm font-bold uppercase tracking-widest px-8 h-14 border-white/20 hover:border-white/40 hover:bg-white/5 gap-2">
                <ExternalLink className="w-4 h-4" />
                Official Source
              </Button>
            </a>
          </div>
        </motion.div>
      </div>

      {/* Schedule Dates */}
      <div className="relative z-10 container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold uppercase tracking-widest mb-10 flex items-center gap-3"
          >
            <Calendar className="w-6 h-6 text-primary" />
            Festival Schedule
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {dates.map((date, idx) => {
              const parsedDate = parseISO(date);
              const eventCount = URBAN_FILM_FESTIVAL_SCHEDULE[date]?.length || 0;
              const cover = date === "2026-09-05" ? `${base}/transcend-film.jpg` : `${base}/uff-logo.png`;
              
              return (
                <motion.div
                  key={date}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link href={`/urban-film-festival-miami/${date}`}>
                    <div className="group relative block rounded-2xl bg-black/40 border border-white/10 backdrop-blur-sm hover:bg-black/60 hover:border-primary/50 transition-all duration-500 overflow-hidden cursor-pointer h-full">
                      <div className="h-52 overflow-hidden bg-white/95">
                        <img src={cover} alt="" className={`w-full h-full group-hover:scale-105 transition-transform duration-500 ${date === "2026-09-05" ? "object-cover object-top" : "object-contain p-5"}`} />
                      </div>
                      {/* Hover effect gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <div className="relative z-10 flex flex-col p-8">
                        <div className="mb-4">
                          <p className="text-sm font-mono text-primary mb-2 tracking-widest uppercase">
                            Day {idx + 1}
                          </p>
                          <h3 className="text-3xl md:text-4xl font-bold uppercase tracking-tight">
                            {format(parsedDate, "EEEE")}
                          </h3>
                          <p className="text-xl text-muted-foreground mt-1">
                            {format(parsedDate, "MMMM do, yyyy")}
                          </p>
                        </div>
                        
                        <div className="mt-auto pt-8 flex items-center justify-between border-t border-white/10">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                            <Video className="w-4 h-4" />
                            <span>{eventCount} Screenings & Events</span>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                            <ArrowRight className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Attribution */}
      <div className="relative z-10 container mx-auto px-4 mt-20 text-center">
        <p className="text-xs font-mono text-muted-foreground/60 uppercase tracking-widest">
          Data provided by Eventive & Urban Film Festivals.
        </p>
      </div>
    </div>
  );
}
